import { ShieldCheck, Lock, EyeOff, UserCheck } from 'lucide-react';
import { usePageSEO } from '../hooks/usePageSEO';

export function PrivacyPolicy() {
  usePageSEO({
    title: 'Privacy & Confidentiality Policy | WellZora Herbal Care',
    description: 'Learn how WellZora Herbal Care protects your privacy, personal health data, and confidential packaging for herbal deliveries across Pakistan.',
    canonical: 'https://wellzoraherbalcare.com/privacy-policy',
  });

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 sm:p-12">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-emerald-50 text-primary rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
            <Lock className="w-3.5 h-3.5" /> Privacy & Discretion
          </div>

          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight mb-4">
            Privacy & Confidentiality Policy
          </h1>
          <p className="text-gray-500 text-sm mb-8 pb-6 border-b border-gray-100">
            Last Updated: September 2026 • WellZora Herbal Care Pakistan
          </p>

          <div className="space-y-8 text-gray-700 leading-relaxed">
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                <EyeOff className="w-5 h-5 text-primary" /> 1. 100% Discreet & Confidential Packaging
              </h2>
              <p>
                At WellZora Herbal Care, we deeply understand the sensitive and personal nature of wellness and herbal remedies. All packages sent via courier (TCS, Leopards, Trax, Call Courier) are delivered in plain, unmarked, tamper-evident parcel flyers. Neither the outer box nor the delivery label mentions the specific herbal product names, ensuring complete privacy from doorstep to delivery.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-primary" /> 2. Information We Collect
              </h2>
              <p className="mb-2">
                When placing an order or requesting a consultation, we only collect essential customer information needed to fulfill your Cash on Delivery (COD) order:
              </p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600">
                <li>Full Customer Name</li>
                <li>Delivery Street Address, City & Postal details</li>
                <li>Active Contact Phone / WhatsApp Number for delivery confirmation</li>
                <li>Email address (optional, for invoice and status notifications)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-primary" /> 3. Data Protection & Zero Third-Party Sharing
              </h2>
              <p>
                We maintain a strict zero-spam, zero-reselling policy. Your personal phone numbers and delivery details are strictly utilized to coordinate order dispatch, track courier status, and provide customer support. We never sell, rent, or trade customer contact details with external marketing agencies.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">4. WhatsApp Consultation Privacy</h2>
              <p>
                Any health symptoms, lifestyle queries, or medical history shared during private WhatsApp consultations with our herbal practitioners are treated with professional doctor-patient confidentiality and are never shared publicly or stored outside encrypted communication channels.
              </p>
            </section>

            <section className="pt-6 border-t border-gray-100">
              <h2 className="text-lg font-bold text-gray-900 mb-2">Questions or Data Inquiries?</h2>
              <p className="text-sm text-gray-600">
                If you wish to update, modify, or permanently delete your contact information from our dispatch records, please contact us at <a href="mailto:wellzoraherbalcare@gmail.com" className="text-primary font-medium hover:underline">wellzoraherbalcare@gmail.com</a> or message our official WhatsApp helpline at <strong>0305-6367673</strong>.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
